/**
 * The live conversation pane (pane B) — docs/design-handoff/README.md "3.
 * Live conversation (pane B)". Composes the header, triage strip, live-call
 * strip, transcript and composer around whichever thread is selected.
 *
 * The governing rule (README "Build against the operations layer, not the
 * console"): every action here is exactly one call to `ops`, or opens a
 * modal Task 17 owns via `openModal` — nothing decides anything itself. On
 * a refusal from the operations layer (`OpsError`), this pane shows the
 * layer's own `.message` rather than inventing copy or silently swallowing
 * it (see the `opError` banner below).
 *
 * `store.threads` only carries `ThreadSummary` (no messages — see
 * packages/shared/src/types.ts). The full `ThreadDetail` — including
 * `msgs`, the transcript's own data — comes from `../../lib/useThreadDetail`
 * (task-42, fix round 2): read that module's header comment for why a
 * plain `[activeId]`-keyed fetch used to leave this transcript silently
 * stale whenever the OTHER party sent a message (the SSE `thread` event
 * that arrives for it only ever carries a `ThreadSummary`, never `msgs`,
 * and `activeId` never changes just because a reply landed) — the owner
 * had to hard-refresh to read replies. Header fields (owner, tier, ticket,
 * band, liveness, call…) prefer the live `ThreadSummary` from the store
 * when one is present (`useThreadDetail`'s own `storeSummary`), since
 * that's what `subscribeStream`'s SSE `thread` events keep fresh in real
 * time; the merged detail is the fallback for a thread the store's current
 * scope doesn't include.
 */
import { useEffect, useRef, useState } from 'react'
import { MEDIA_MAX_BYTES } from '@communicator/shared'
import type { Message, ThreadSummary } from '@communicator/shared'
import { ops, OpsError } from '../../lib/ops'
import { createTypingReporter, FOCUS_HEARTBEAT_MS, type TypingReporter } from '../../lib/presenceBeacon'
import { activity } from '../../lib/activity'
import { useThreadDetail } from '../../lib/useThreadDetail'
import { selectPref, useStore } from '../../store'
import { Composer, type PendingAttachment } from './Composer'
import { CallSummary } from './CallSummary'
import { isComposerSuppressed, isTransportDown, isUnresolvedTier, readOnlyReason } from './format'
import { looksLikePhoneNumber } from '../rail/format'
import { LiveCallStrip } from './LiveCallStrip'
import { Transcript } from './Transcript'
import { ThreadHeader } from './ThreadHeader'
import { TriageStrip } from './TriageStrip'

export function ThreadPane() {
  const activeId = useStore((s) => s.activeId)
  const peers = useStore((s) => s.peers)
  const svc = useStore((s) => s.svc)
  const cfg = useStore((s) => s.cfg)
  // THIS thread's draft, not the console's. See the store's `drafts` comment
  // for what a single shared one did: text typed on one call turned up in the
  // next one you clicked.
  const draft = useStore((s) => (s.activeId ? (s.drafts[s.activeId] ?? '') : ''))
  const callNote = useStore((s) => (s.activeId ? (s.callNotes[s.activeId] ?? '') : ''))
  const setDraft = useStore((s) => s.setDraft)
  const setCallNote = useStore((s) => s.setCallNote)
  const selfId = useStore((s) => s.me?.id ?? null)
  const selfName = useStore((s) => s.me?.name ?? null)
  // The viewer's own extension, which is what lets the live-call strip say
  // "You" truthfully rather than over anyone's call. It lives on the STAFF
  // ROW, set by an admin in Settings -> Users, not in personal preferences:
  // one person sets the floor's extensions once, instead of everyone being
  // told to go and find a settings field before the bar can tell the truth
  // for any of them.
  const selfExt = useStore((s) => s.me?.ext ?? null)
  const set = useStore((s) => s.set)
  const openModal = useStore((s) => s.openModal)
  // 2026-08-24 design: the viewer's own message-timestamp zone, per-user
  // (`p.tz`) rather than the org-wide `v.tz` above `cfg` already carries —
  // that one governs what gets written into a Halo note, this one only ever
  // changes what this one person sees. Falls back to 'auto' (the pref's own
  // default) so a fresh account is correct before ever opening Settings.
  const tz = useStore((s) => selectPref(s.prefs, 'p.tz', 'auto'))
  // Task 7 (multiple SMS lines): the header's "via <label>" chip only ever
  // appears once there is more than one line to disambiguate between — see
  // ThreadHeader's own `showLine` prop doc comment. `store.lines` is `null`
  // both before the post-sign-in fetch resolves and if it fails, and `?? 0`
  // treats that the same as "nothing to disambiguate," which is the correct
  // degraded behaviour either way.
  const showLine = useStore((s) => (s.lines?.length ?? 0) > 1)

  const { detail, storeSummary, refetch } = useThreadDetail(activeId)
  const [opError, setOpError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  // Task 8: images already uploaded (via `ops.uploadMedia`) and waiting to
  // ride the NEXT send on the currently selected thread. Deliberately plain
  // `useState`, not a `drafts`-style store field keyed by thread — unlike a
  // typed-but-unsent draft, an upload nobody has sent yet has no reason to
  // survive navigating away from the thread (there is no "come back to it
  // later" affordance for an attachment the way there is for typed text), so
  // it resets on every thread switch below exactly like `opError` does.
  const [pendingMedia, setPendingMedia] = useState<PendingAttachment[]>([])
  const typingReporterRef = useRef<TypingReporter | null>(null)

  useEffect(() => {
    setOpError(null)
    setPendingMedia([])
    // The draft is deliberately NOT cleared here any more. It belongs to the
    // thread you typed it in and is waiting there when you come back — which
    // is the whole point of keying drafts by thread. Clearing on switch was
    // the other half of the same bug: text leaked onto the next thread AND
    // was destroyed on the way out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId])

  // Task 35: the "which thread they have focused" half of the presence
  // beacon. Reports immediately on selection, then again on a
  // `FOCUS_HEARTBEAT_MS` heartbeat for as long as this thread stays
  // selected — the server's reading TTL (services/ops/src/presence.ts) is
  // several multiples of this interval on purpose, so one dropped request
  // over a flaky connection never flips the outline badge off. Fire-and-
  // forget throughout: a failed beacon report is never something a
  // dispatcher should see an error banner over (ops.ts's own doc comment on
  // `presenceBeacon`).
  useEffect(() => {
    if (!activeId) {
      // An explicit deselect is a definite signal, not a guess — clear it
      // immediately rather than leaving the previous thread's badge to
      // fade out on the server's own TTL, the same anti-staleness reasoning
      // the TTLs themselves exist for.
      void ops.presenceBeacon(null, false).catch(() => {})
      return
    }
    // ONLY WHILE SOMEBODY IS ACTUALLY HERE.
    //
    // This heartbeat used to fire every fifteen seconds for as long as the tab
    // existed. Andrew's laptop sat locked on his desk for five hours still
    // reporting "I am reading this thread" — which marks it read, which
    // cleared the unread pulse on his phone for a message he had never seen.
    //
    // An open tab is not a person (see lib/activity.ts). Going away reports
    // `null` once, so the server drops both the reading badge and any reason
    // to keep marking this thread read.
    // READING requires the window to be in front, not merely open. A console
    // behind another app is still `visible` to the browser, so reporting on
    // visibility alone meant somebody with the window buried counted as
    // reading this thread — and the push routing skips paging whoever is
    // already reading it. That is why it alerted when minimised and not when
    // simply covered by something else.
    function reportFocus() {
      const reading = activity.isActive() && activity.isFocused()
      void ops.presenceBeacon(reading ? activeId : null, false).catch(() => {})
    }
    function onActivityChange() {
      reportFocus()
    }

    reportFocus()
    const unsubscribe = activity.subscribe(onActivityChange)
    const heartbeat = setInterval(reportFocus, FOCUS_HEARTBEAT_MS)
    return () => {
      unsubscribe()
      clearInterval(heartbeat)
    }
  }, [activeId])

  // The "typing" half. One reporter per selected thread — switching threads
  // disposes the previous one (cancelling any pending debounced "stopped
  // typing" report for a thread the composer no longer targets) rather than
  // letting it fire late against the wrong thread id.
  useEffect(() => {
    if (!activeId) {
      typingReporterRef.current = null
      return
    }
    const reporter = createTypingReporter((typing) => {
      void ops.presenceBeacon(activeId, typing).catch(() => {})
    })
    typingReporterRef.current = reporter
    return () => {
      reporter.dispose()
      typingReporterRef.current = null
    }
  }, [activeId])

  const activeSummary: ThreadSummary | undefined = storeSummary ?? detail ?? undefined

  // ABOVE THE EARLY RETURN, with every other hook. Declared below the guard
  // it runs only when a thread is selected, so React sees a different hook
  // count between renders and throws "Rendered fewer hooks than expected" —
  // which takes out the whole console, not just this pane. This exact
  // mistake has been made in this file before.
  const [composerMode, setComposerMode] = useState<'note' | 'text'>('note')
  const isVoice = (storeSummary ?? detail)?.channel === 'voice'

  if (!activeId || !activeSummary) {
    return (
      <div data-testid="thread-pane-empty" className="flex flex-1 items-center justify-center text-[13px] text-mid">
        Select a thread to see the conversation.
      </div>
    )
  }

  // A plain non-optional binding — TS does not carry the guard above's
  // narrowing into the nested function declarations below (they could in
  // principle run later, after a hypothetical reassignment), so the
  // handlers close over this instead of the still-optional `activeSummary`.
  const summary: ThreadSummary = activeSummary

  /** Every mutating handler runs through this: call one operation, refresh the detail on success, surface the layer's own refusal copy on failure. */
  async function runOp(fn: () => Promise<unknown>): Promise<boolean> {
    setOpError(null)
    try {
      await fn()
      await refetch()
      return true
    } catch (err) {
      setOpError(err instanceof OpsError ? err.message : 'Something went wrong. Try again.')
      return false
    }
  }

  function handleTakeOver() {
    void runOp(() => ops.call('claim', { threadId: summary.id }))
  }

  function handleSendCode() {
    openModal('otp')
  }

  function handleAttachTicket() {
    openModal('ticket')
  }

  function handleHandOff() {
    openModal('handoff')
  }

  function handleCloseOrDismiss() {
    openModal('close')
  }

  // "Match to contact" and "Create contact" both open the Match modal
  // (Task 17) — like the Attach-ticket modal, which combines "create new"
  // and "pick existing" in one surface (README §5), the Match modal is the
  // natural place for both flows since neither has the extra fields (a
  // contact to pick, a name to create) that this strip can collect inline.
  function handleMatch() {
    openModal('match')
  }

  function handleCreateContact() {
    openModal('match')
  }

  function handlePark() {
    void runOp(() => ops.call('resolve-identity', { threadId: summary.id, action: 'park', writeBackToHalo: false, aliasHandle: false }))
  }

  function handleDismissTriage() {
    void runOp(() => ops.call('resolve-identity', { threadId: summary.id, action: 'dismiss', writeBackToHalo: false, aliasHandle: false }))
  }

  function handleSend() {
    const body = draft.trim()
    // A NOTE IS NOT A SEND. Same box, same enter key, entirely different
    // operation and audience: `add-note` writes to our own timeline and
    // reaches no customer. Routing both through one handler keeps the typing
    // experience identical, which is the point — a dispatcher mid-call should
    // not have to think about which control she is in.
    // `isVoice &&` is load-bearing. The mode state defaults to 'note' and
    // exists for every thread, but the toggle is only OFFERED on voice — so
    // without this guard an SMS thread would quietly write a note instead of
    // texting the customer, with the composer showing no sign anything was
    // different. Caught by the existing send tests.
    if (isVoice && composerMode === 'note') {
      if (!body || sending) return
      setSending(true)
      setOpError(null)
      setDraft(summary.id, '')
      void ops
        .call('add-note', { threadId: summary.id, body })
        .then(() => refetch())
        .catch((err) => {
          // Put it back. Losing what somebody typed mid-call is worse than
          // showing it twice, and these fragments are unrepeatable — she was
          // listening when she wrote it.
          setDraft(summary.id, body)
          setOpError(err instanceof OpsError ? err.message : 'The note was not saved. Try again.')
        })
        .finally(() => setSending(false))
      return
    }

    // TWO GUARDS, because this sends a real text to a real customer.
    //
    // The box used to keep its text until the send AND a full thread refetch
    // had both finished. On a phone that reads as "nothing happened", and the
    // natural response is to tap again — which sent the message twice.
    if (!body || sending) return

    setSending(true)
    setOpError(null)
    // Cleared immediately, so the tap visibly did something. Put back if the
    // send fails: losing what somebody typed is worse than showing it twice.
    setDraft(summary.id, '')

    // `mediaIds` is only ever included when there is something to include —
    // NOT `mediaIds: []` on every send. `SendArgs.mediaIds` is `.optional()`
    // (packages/shared/src/operations.ts), and this keeps the wire body for
    // every ordinary text-only send byte-for-byte what it always was, rather
    // than teaching every caller of `ops.call('send', ...)` (this pane's own
    // tests included) to expect a new always-present empty array.
    const mediaIds = pendingMedia.map((m) => m.id)
    void ops
      .call('send', mediaIds.length > 0 ? { threadId: summary.id, body, mediaIds } : { threadId: summary.id, body })
      .then(() => {
        // Cleared only on success, same rule as the draft: a failed send
        // leaves the chips exactly where they were so retrying (the Enter
        // key/Send button again) resends the SAME attachments rather than
        // silently dropping them.
        setPendingMedia([])
        return refetch()
      })
      .catch((err) => {
        setDraft(summary.id, body)
        setOpError(err instanceof OpsError ? err.message : 'The message was not sent. Try again.')
      })
      .finally(() => setSending(false))
  }

  function handleRetry(message: Message) {
    // FIX (review round 1, finding 1): this used to resend `body` only. A
    // `notSent` MMS message still carries its `media` refs on read (the
    // failed send's `POST /ops/media` succeeded — only the transport hop
    // failed), so retrying with body-only silently dropped the image the
    // customer never got — exactly the failure class this feature exists to
    // prevent. The ids still belong to this thread (ops's ownership check
    // passes) and the media rows already exist, so no re-upload is needed —
    // just carry the same refs forward. The `mediaIds` KEY IS OMITTED
    // entirely (not sent as `undefined`/`[]`) when there is nothing to
    // carry — same "only present when non-empty" rule `handleSend`'s own
    // `mediaIds` follows, so a text-only retry's wire body stays exactly
    // what it always was.
    const mediaIds = message.media && message.media.length > 0 ? message.media.map((m) => m.id) : undefined
    void runOp(() =>
      ops.call(
        'send',
        mediaIds ? { threadId: summary.id, body: message.body, mediaIds } : { threadId: summary.id, body: message.body },
      ),
    )
  }

  /**
   * Task 8: the paperclip's file input resolves straight to here — `ThreadPane`
   * owns the upload (README's "Build against the operations layer" rule:
   * `Composer` never talks to `ops` itself). Pre-checks size client-side
   * against the SAME `MEDIA_MAX_BYTES` the server enforces
   * (packages/shared/src/media.ts) purely so an obviously-too-big file never
   * makes the round trip at all — the server's own `too-large` refusal is
   * still the real gate for anything this check misses or gets wrong.
   */
  function handleAttachFile(file: File) {
    if (file.size > MEDIA_MAX_BYTES) {
      setOpError('That image is larger than 5MB.')
      return
    }
    setOpError(null)
    const filename = file.name
    void ops
      .uploadMedia(summary.id, file)
      .then((media) => {
        setPendingMedia((prev) => [...prev, { id: media.id, filename }])
      })
      .catch((err) => {
        setOpError(err instanceof OpsError ? err.message : 'The image could not be uploaded. Try again.')
      })
  }

  function handleRemovePending(id: string) {
    setPendingMedia((prev) => prev.filter((m) => m.id !== id))
  }

  // Texting the caller needs a number to text. `request-otp` resolves one the
  // same way; when a call arrives with none (a withheld number, an internal
  // leg), the tab says why rather than failing at the moment of sending.
  const textUnavailable =
    isVoice && !looksLikePhoneNumber(summary.who) && summary.client === null
      ? 'No number on this call to text'
      : null

  const composerReadOnlyReason = readOnlyReason(summary, selfId)
  const transportDown = isTransportDown(summary.channel, svc)
  const suppressed = isComposerSuppressed(summary.channel, summary.call?.state)

  return (
    <div data-testid="thread-pane" className="flex h-full min-h-0 flex-1 flex-col">
      <ThreadHeader
        thread={summary}
        peers={peers}
        cfg={cfg}
        selfId={selfId}
        showLine={showLine}
        onTakeOver={handleTakeOver}
        onSendCode={handleSendCode}
        onAttachTicket={handleAttachTicket}
        onHandOff={handleHandOff}
        onCloseOrDismiss={handleCloseOrDismiss}
        onChangeIdentity={handleMatch}
      />

      {opError && (
        <div role="alert" data-testid="op-error" className="bg-bad-subtle px-4 py-2 text-[12px] text-bad">
          {opError}
        </div>
      )}

      {isUnresolvedTier(summary.tier) && summary.state !== 'closed' && (
        <TriageStrip onMatch={handleMatch} onCreateContact={handleCreateContact} onPark={handlePark} onDismiss={handleDismissTriage} />
      )}

      {summary.call?.state === 'live' && (
        <LiveCallStrip
          openedAt={summary.openedAt}
          // `handler` is the extension actually holding the call (Call
          // Control writes it from the participant's `dn`); `ext` is the
          // thread's own and is the same value for a Call Control call, so
          // it is only a fallback for a thread that predates it.
          holderExt={summary.call.handler ?? summary.call.ext}
          holderName={summary.call.handlerName}
          selfExt={selfExt}
          selfName={selfName}
        />
      )}

      <Transcript messages={detail?.msgs ?? []} onRetry={handleRetry} tz={tz} />

      {/* A finished call gets its story where the composer would be — see
          CallSummary on why disabling the composer is not enough. */}
      {summary.channel === 'voice' && summary.call && summary.call.state !== 'live' ? (
        <CallSummary
          // KEYED BY THREAD, so React gives each call its own instance rather
          // than reusing one across switches. Everything this component keeps
          // locally — whether it has been logged, the billable box, a save
          // error, whether its draft was seeded — is about ONE call, and a
          // reused instance carried all of it to the next one. The typed note
          // is the single thing that must survive the remount, which is why
          // it lives in the store and comes back in as a prop.
          key={summary.id}
          call={summary.call}
          who={summary.who}
          threadId={summary.id}
          hasTicket={summary.ticket !== null}
          // The contact is the whole gate. Halo refuses to CLOSE a ticket that sits on the
          // default Client/Site (400, verified live on scratch 64296) — but a ticket created
          // with a user_id INHERITS that contact's client and site, and then closes fine
          // (verified live on scratch 64298: contact 1214 -> client 54/site 63 -> status 9
          // accepted). So requiring thread.clientId here was over-strict: matched voice
          // threads routinely carry no clientId of their own, and the first live test hid
          // the button from the exact call it was built for.
          matched={summary.haloContactId !== null}
          note={callNote}
          onNoteChange={(value) => setCallNote(summary.id, value)}
          // Every note on a voice thread belongs to its one call, so no
          // windowing by time is needed — and doing it by timestamp would
          // silently drop the note typed a beat after the caller hung up,
          // which is exactly when the useful ones get written.
          notes={(detail?.msgs ?? []).filter((m) => m.from === 'note').map((m) => m.body)}
        />
      ) : null}

      <Composer
        channel={summary.channel}
        liveness={summary.liveness}
        transportDown={transportDown}
        readOnlyReason={composerReadOnlyReason}
        suppressed={suppressed}
        draft={draft}
        onDraftChange={(value) => {
          setDraft(summary.id, value)
          typingReporterRef.current?.notifyKeystroke()
        }}
        onSend={handleSend}
        sending={sending}
        mode={isVoice ? composerMode : undefined}
        onModeChange={isVoice ? setComposerMode : undefined}
        textUnavailable={textUnavailable}
        pendingMedia={pendingMedia}
        onAttachFile={handleAttachFile}
        onRemovePending={handleRemovePending}
      />
    </div>
  )
}
